/**
 * Centralized date/time helpers for Lesson Launchpad.
 *
 * Date keys (YYYY-MM-DD) always use the teacher's local calendar date.
 * Birthday matching uses stored month/day only — never UTC conversion.
 *
 * Recommended usage:
 *   getLocalDateKey()           — today's local date key
 *   getLocalDateKey(date)       — local date key for any date input
 *   isBirthdayOnDate(bday, date) — calendar birthday match
 *   formatTimeFromHHmm("14:30", timeFormat) — display stored schedule times
 *   resolveTimeFormat(user.settings) — "12h" | "24h" from user prefs
 */

export const TIME_FORMAT_12H = "12h";
export const TIME_FORMAT_24H = "24h";
export const DEFAULT_TIME_FORMAT = TIME_FORMAT_12H;

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isDateKey = (value) =>
  typeof value === "string" && DATE_KEY_PATTERN.test(value.trim());

export const formatDateKeyFromParts = (year, month, day) => {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return "";
  }

  return [
    String(year).padStart(4, "0"),
    String(month + 1).padStart(2, "0"),
    String(day).padStart(2, "0"),
  ].join("-");
};

export const parseDateKeyParts = (dateKey) => {
  if (typeof dateKey !== "string") return null;
  const match = dateKey.trim().match(DATE_KEY_PATTERN);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);

  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

export const parseDateKeyToLocalDate = (dateKey) => {
  const parts = parseDateKeyParts(dateKey);
  return parts ? new Date(parts.year, parts.month, parts.day) : null;
};

/** Normalize any supported date input to local midnight. */
export const normalizeToLocalDate = (input) => {
  if (input instanceof Date) {
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }

  if (typeof input === "string") {
    const parsedDateKey = parseDateKeyToLocalDate(input);
    if (parsedDateKey) return parsedDateKey;
  }

  if (
    isPlainObject(input) &&
    Number.isFinite(input.year) &&
    Number.isFinite(input.month) &&
    Number.isFinite(input.day)
  ) {
    return new Date(input.year, input.month, input.day);
  }

  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** Local calendar date at midnight (alias for normalizeToLocalDate with no input). */
export const getLocalToday = () => normalizeToLocalDate(new Date());

/**
 * YYYY-MM-DD in the teacher's local timezone.
 * Compatible with legacy en-CA toLocaleDateString output.
 */
export const getLocalDateKey = (input) => {
  const date = input === undefined ? getLocalToday() : normalizeToLocalDate(input);
  return formatDateKeyFromParts(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getDateKeyFromInput = (input) => {
  if (typeof input === "string") {
    const parts = parseDateKeyParts(input);
    if (parts) return formatDateKeyFromParts(parts.year, parts.month, parts.day);
  }

  if (
    isPlainObject(input) &&
    Number.isFinite(input.year) &&
    Number.isFinite(input.month) &&
    Number.isFinite(input.day)
  ) {
    return formatDateKeyFromParts(input.year, input.month, input.day);
  }

  if (input instanceof Date && !Number.isNaN(input.getTime())) {
    return getLocalDateKey(input);
  }

  return "";
};

export const isDateKeyInRange = (dateKey, startKey, endKey = startKey) => {
  const target = getDateKeyFromInput(dateKey);
  const start = getDateKeyFromInput(startKey);
  const end = getDateKeyFromInput(endKey || startKey);

  if (!target || !start || !end) return false;

  const rangeStart = start <= end ? start : end;
  const rangeEnd = start <= end ? end : start;
  return target >= rangeStart && target <= rangeEnd;
};

export const doDateKeyRangesOverlap = (firstStartKey, firstEndKey, secondStartKey, secondEndKey) => {
  const firstStart = getDateKeyFromInput(firstStartKey);
  const firstEnd = getDateKeyFromInput(firstEndKey || firstStartKey);
  const secondStart = getDateKeyFromInput(secondStartKey);
  const secondEnd = getDateKeyFromInput(secondEndKey || secondStartKey);

  if (!firstStart || !firstEnd || !secondStart || !secondEnd) return false;

  const normalizedFirstStart = firstStart <= firstEnd ? firstStart : firstEnd;
  const normalizedFirstEnd = firstStart <= firstEnd ? firstEnd : firstStart;
  const normalizedSecondStart = secondStart <= secondEnd ? secondStart : secondEnd;
  const normalizedSecondEnd = secondStart <= secondEnd ? secondEnd : secondStart;

  return normalizedFirstStart <= normalizedSecondEnd && normalizedFirstEnd >= normalizedSecondStart;
};

export const enumerateDateKeysInRange = (startKey, endKey = startKey, maxDays = 366) => {
  const startDate = parseDateKeyToLocalDate(startKey);
  const endDate = parseDateKeyToLocalDate(endKey || startKey);
  if (!startDate || !endDate || maxDays <= 0) return [];

  const rangeStart = startDate <= endDate ? startDate : endDate;
  const rangeEnd = startDate <= endDate ? endDate : startDate;
  const keys = [];
  const cursor = new Date(rangeStart);

  while (cursor <= rangeEnd && keys.length < maxDays) {
    keys.push(getLocalDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return keys;
};

/** Parse YYYY-MM-DD birthday string to { month: 0-11, day: 1-31 }. */
export const parseBirthdayParts = (birthdayString) => {
  if (typeof birthdayString !== "string") return null;
  const match = birthdayString.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (month < 0 || month > 11 || day < 1 || day > 31) return null;

  return { month, day };
};

/** True when a stored birthday falls on the active local calendar date. */
export const isBirthdayOnDate = (birthdayString, activeDateInput = new Date()) => {
  const parts = parseBirthdayParts(birthdayString);
  if (!parts) return false;

  const activeDate = normalizeToLocalDate(activeDateInput);
  return parts.month === activeDate.getMonth() && parts.day === activeDate.getDate();
};

const getOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) return "th";
  const lastDigit = day % 10;
  if (lastDigit === 1) return "st";
  if (lastDigit === 2) return "nd";
  if (lastDigit === 3) return "rd";
  return "th";
};

/** e.g. "May 1, 2026" */
export const formatDate = (input, locale = "en-US") => {
  const date = normalizeToLocalDate(input);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric" });
};

/** e.g. "May 1" */
export const formatShortDate = (input, locale = "en-US") => {
  const date = normalizeToLocalDate(input);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
};

/** e.g. "Friday - May 29th, 2026" */
export const formatLongDate = (input, locale = "en-US") => {
  const date = normalizeToLocalDate(input);
  if (Number.isNaN(date.getTime())) return "";

  const weekday = date.toLocaleDateString(locale, { weekday: "long" });
  const monthName = date.toLocaleDateString(locale, { month: "long" });
  const day = date.getDate();
  const year = date.getFullYear();

  return `${weekday} - ${monthName} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

/** Normalize user/class clock format values to "12h" | "24h". */
export const normalizeTimeFormat = (value, fallback = DEFAULT_TIME_FORMAT) => {
  if (value === TIME_FORMAT_24H || value === "24hr") return TIME_FORMAT_24H;
  if (value === TIME_FORMAT_12H || value === "12hr") return TIME_FORMAT_12H;
  return fallback;
};

/** Resolve display time format from user settings (defaults to 12-hour). */
export const resolveTimeFormat = (userSettings = {}) =>
  normalizeTimeFormat(userSettings?.timeFormat);

/**
 * Resolve clock display format: class-level override wins, then user setting.
 * Maps legacy clockFormat ("12hr"|"24h") and timeFormat ("12h"|"24h").
 */
export const resolveClockDisplayFormat = ({
  userSettings = {},
  classClockFormat = null,
} = {}) => {
  if (classClockFormat === "24h" || classClockFormat === "24hr") return TIME_FORMAT_24H;
  if (classClockFormat === "12hr" || classClockFormat === "12h") return TIME_FORMAT_12H;
  return resolveTimeFormat(userSettings);
};

/** Convert clock format enum to legacy Clock component values. */
export const toLegacyClockFormat = (timeFormat) =>
  normalizeTimeFormat(timeFormat) === TIME_FORMAT_24H ? "24h" : "12hr";

export const parseTimeToMinutes = (timeString) => {
  if (typeof timeString !== "string" || !timeString.includes(":")) return null;
  const [hours, minutes] = timeString.split(":").map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const formatMinutesToHHmm = (minutesFromMidnight) => {
  if (!Number.isFinite(minutesFromMidnight)) return "";
  const normalizedMinutes = ((Math.round(minutesFromMidnight) % 1440) + 1440) % 1440;
  const hours = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export const addMinutesToHHmm = (timeString, minutesToAdd = 0) => {
  const minutesFromMidnight = parseTimeToMinutes(timeString);
  if (minutesFromMidnight === null) return "";
  return formatMinutesToHHmm(minutesFromMidnight + (Number(minutesToAdd) || 0));
};

/** Format minutes-from-midnight for display (schedule times stored as HH:mm). */
export const formatTimeFromMinutes = (
  minutesFromMidnight,
  timeFormat = DEFAULT_TIME_FORMAT
) => {
  if (!Number.isFinite(minutesFromMidnight)) return "";

  const hours = Math.floor(minutesFromMidnight / 60);
  const minutes = minutesFromMidnight % 60;
  const paddedMinutes = String(minutes).padStart(2, "0");

  if (normalizeTimeFormat(timeFormat) === TIME_FORMAT_24H) {
    return `${String(hours).padStart(2, "0")}:${paddedMinutes}`;
  }

  const suffix = hours >= 12 ? "PM" : "AM";
  const normalizedHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${normalizedHour}:${paddedMinutes} ${suffix}`;
};

/** Format stored HH:mm schedule time for display. */
export const formatTimeFromHHmm = (timeString, timeFormat = DEFAULT_TIME_FORMAT) => {
  const minutesFromMidnight = parseTimeToMinutes(timeString);
  if (minutesFromMidnight === null) return timeString;
  return formatTimeFromMinutes(minutesFromMidnight, timeFormat);
};

/** Format timeline hour label (0–23). */
export const formatTimelineHourLabel = (hour, timeFormat = DEFAULT_TIME_FORMAT) => {
  if (normalizeTimeFormat(timeFormat) === TIME_FORMAT_24H) {
    return `${String(hour).padStart(2, "0")}:00`;
  }

  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${normalizedHour}:00 ${suffix}`;
};

/** Format a time range from stored HH:mm values. */
export const formatTimeRange = (startTime, endTime, timeFormat = DEFAULT_TIME_FORMAT) =>
  `${formatTimeFromHHmm(startTime, timeFormat)} - ${formatTimeFromHHmm(endTime, timeFormat)}`;

/** Format a Date object's wall-clock time for display. */
export const formatTime = (dateInput, timeFormat = DEFAULT_TIME_FORMAT, { showSeconds = false } = {}) => {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  if (normalizeTimeFormat(timeFormat) === TIME_FORMAT_24H) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    if (!showSeconds) return `${hours}:${minutes}`;
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const paddedMinutes = String(minutes).padStart(2, "0");

  if (!showSeconds) {
    return `${formattedHours}:${paddedMinutes} ${ampm}`;
  }

  const paddedSeconds = String(seconds).padStart(2, "0");
  return `${formattedHours}:${paddedMinutes}:${paddedSeconds} ${ampm}`;
};
